/**
 * Media Hub content — SINGLE SOURCE OF TRUTH.
 *
 * Powers `/media` (rendered by `pages/MediaHubPage.jsx`). Adding new media
 * = append an entry to the appropriate array below. No component edits.
 *
 * Designed so future CMS / database integration is a drop-in: the same
 * shape can be returned by an API endpoint and merged client-side.
 *
 * Categories: 'video' | 'poster' | 'pdf' | 'clinic-visit' | 'patient-guide' | 'campaign'
 */

/* ── Featured videos ────────────────────────────────────────────────────── */
export const MEDIA_VIDEOS = [
  {
    id: 'v-overview',
    category: 'video',
    title: 'VietClinics — Healthcare Made Simple in Vietnam',
    description: 'A short introduction to how VietClinics coordinates care for international patients.',
    tag: 'Company Overview',
    duration: '2:45',
    date: 'May 15, 2025',
    thumbnail: 'https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=900',
    url: '#', // replace with YouTube / Vimeo URL when uploaded
  },
  {
    id: 'v-dental-implant',
    category: 'video',
    title: 'Dental Implant Process in Vietnam',
    description: 'A behind-the-scenes look at the dental implant journey with a VietClinics partner.',
    tag: 'Treatment Explained',
    duration: '1:32',
    date: 'May 10, 2025',
    thumbnail: 'https://images.pexels.com/photos/3779694/pexels-photo-3779694.jpeg?auto=compress&cs=tinysrgb&w=900',
    url: '#',
  },
  {
    id: 'v-clinic-hcm',
    category: 'clinic-visit',
    title: 'Inside a Premium Clinic in Ho Chi Minh City',
    description: 'A walkthrough of one of our verified partner clinics in central Ho Chi Minh City.',
    tag: 'Clinic Walkthrough',
    duration: '3:18',
    date: 'May 5, 2025',
    thumbnail: 'https://images.pexels.com/photos/247786/pexels-photo-247786.jpeg?auto=compress&cs=tinysrgb&w=900',
    url: '#',
  },
  {
    id: 'v-patient-support',
    category: 'video',
    title: 'Your Journey, Our Care — Patient Support',
    description: 'See how our concierge team supports international patients from arrival to recovery.',
    tag: 'Patient Support',
    duration: '1:07',
    date: 'Apr 28, 2025',
    thumbnail: 'https://images.pexels.com/photos/4173324/pexels-photo-4173324.jpeg?auto=compress&cs=tinysrgb&w=900',
    url: '#',
  },
];

/* ── Posters & Campaigns ────────────────────────────────────────────────── */
export const MEDIA_POSTERS = [
  {
    id: 'p-dental-campaign',
    category: 'campaign',
    title: 'Dental Care Campaign May 2025',
    tag: 'Campaign',
    image: 'https://images.pexels.com/photos/3845625/pexels-photo-3845625.jpeg?auto=compress&cs=tinysrgb&w=900',
    accent: '#1FA7A1',
  },
  {
    id: 'p-hair-campaign',
    category: 'campaign',
    title: 'Hair Restoration Campaign',
    tag: 'Campaign',
    image: 'https://images.pexels.com/photos/3768131/pexels-photo-3768131.jpeg?auto=compress&cs=tinysrgb&w=900',
    accent: '#5DA9B0',
  },
  {
    id: 'p-ivf-campaign',
    category: 'campaign',
    title: 'IVF Treatment Campaign',
    tag: 'Campaign',
    image: 'https://images.pexels.com/photos/3825539/pexels-photo-3825539.jpeg?auto=compress&cs=tinysrgb&w=900',
    accent: '#E8B7B0',
  },
  {
    id: 'p-medical-tourism',
    category: 'campaign',
    title: 'Medical Tourism Campaign',
    tag: 'Campaign',
    image: 'https://images.pexels.com/photos/1450353/pexels-photo-1450353.jpeg?auto=compress&cs=tinysrgb&w=900',
    accent: '#0f2b55',
  },
];

/* ── PDFs & Guides ──────────────────────────────────────────────────────── */
export const MEDIA_PDFS = [
  {
    id: 'pdf-partner-pack',
    category: 'pdf',
    title: 'VietClinics Partner Pack',
    description: 'Everything you need to know about partnering with us.',
    size: '2.4 MB',
    url: '#',
  },
  {
    id: 'pdf-patient-guide',
    category: 'patient-guide',
    title: 'Patient Treatment Guide',
    description: 'A complete guide for international patients.',
    size: '3.1 MB',
    url: '#',
  },
  {
    id: 'pdf-dental-guide',
    category: 'patient-guide',
    title: 'Dental Treatment Guide',
    description: 'Procedures, care tips and what to expect.',
    size: '2.8 MB',
    url: '#',
  },
  {
    id: 'pdf-medical-travel',
    category: 'patient-guide',
    title: 'Medical Travel Guide to Vietnam',
    description: 'Plan your trip, stay, and recovery with confidence.',
    size: '4.2 MB',
    url: '#',
  },
  {
    id: 'pdf-company-profile',
    category: 'pdf',
    title: 'VietClinics Company Profile',
    description: 'Our mission, vision and healthcare network.',
    size: '1.9 MB',
    url: '#',
  },
];

/* ── Filter tabs (single source of truth for chip nav + section filtering) */
export const MEDIA_FILTERS = [
  { key: 'all',           label: 'All',            icon: 'LayoutGrid' },
  { key: 'video',         label: 'Videos',         icon: 'PlaySquare' },
  { key: 'poster',        label: 'Posters',        icon: 'Image' },
  { key: 'pdf',           label: 'PDFs',           icon: 'FileText' },
  { key: 'clinic-visit',  label: 'Clinic Visits',  icon: 'Building2' },
  { key: 'patient-guide', label: 'Patient Guides', icon: 'BookOpen' },
  { key: 'campaign',      label: 'Campaigns',      icon: 'Megaphone' },
];

/* ── Helpers ────────────────────────────────────────────────────────────── */
/**
 * Filter a media array by the active filter key.
 * 'all' returns everything; otherwise matches `category === key`.
 */
export const filterMedia = (items, key) =>
  key === 'all' ? items : items.filter((m) => m.category === key);
